import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Target, Sun, Utensils, Activity, Pill, FileText } from "lucide-react";

export interface VitalityReportData {
  // Objectif de l'accompagnement
  motif_principal: string;
  objectifs_definis: string;

  // AXE 1 – Hygiène vitale
  axe1_objectif: string;
  axe1_conseils: string;
  axe1_checkboxes: Record<string, boolean>;

  // AXE 2 – Alimentation
  axe2_objectif: string;
  axe2_conseils: string;
  axe2_checkboxes: Record<string, boolean>;

  // AXE 3 – Activité physique
  axe3_objectif: string;
  axe3_recommandations: string;
  axe3_checkboxes: Record<string, boolean>;

  // Compléments / soutiens naturels
  complements: Array<{
    id: string;
    nom: string;
    forme: string;
    posologie: string;
    moment_prise: string;
    duree: string;
  }>;

  // Autres
  analyses_biologiques: string;
  ressources_conseillees: string;
  orientations_professionnels: string;
  remarques_complementaires: string;
}

export const createDefaultVitalityReport = (): VitalityReportData => ({
  motif_principal: "",
  objectifs_definis: "",
  axe1_objectif: "",
  axe1_conseils: "",
  axe1_checkboxes: {},
  axe2_objectif: "",
  axe2_conseils: "",
  axe2_checkboxes: {},
  axe3_objectif: "",
  axe3_recommandations: "",
  axe3_checkboxes: {},
  complements: [{ id: `comp-${Date.now()}`, nom: "", forme: "", posologie: "", moment_prise: "", duree: "" }],
  analyses_biologiques: "",
  ressources_conseillees: "",
  orientations_professionnels: "",
  remarques_complementaires: "",
});

// Checkboxes pour Axe 1 - Hygiène vitale
export const AXE1_CHECKBOXES = [
  { id: "repos_reguliers", label: "Mettre en place des temps de repos réguliers dans la journée afin de soutenir le système nerveux." },
  { id: "journees_equilibrees", label: "Organiser les journées de manière plus équilibrée en alternant temps d'activité et temps de pause." },
  { id: "horaires_sommeil", label: "Respecter des horaires de coucher et de lever réguliers pour favoriser un sommeil réparateur." },
  { id: "rituel_coucher", label: "Instaurer un rituel apaisant avant le coucher (lecture, respiration, relaxation)." },
  { id: "limiter_ecrans", label: "Limiter l'exposition aux écrans en soirée, idéalement au moins une heure avant le coucher." },
  { id: "identifier_stress", label: "Identifier les principales sources de stress du quotidien afin de mieux les anticiper." },
  { id: "temps_pour_soi", label: "S'accorder chaque jour un temps pour soi, même court, dédié à la détente." },
  { id: "respiration_consciente", label: "Pratiquer quotidiennement une respiration consciente pour favoriser l'apaisement." },
  { id: "ecoute_emotions", label: "Être à l'écoute de ses émotions et respecter ses limites physiques et psychiques." },
  { id: "avancer_progressivement", label: "Avancer progressivement dans les changements, sans recherche de perfection." },
];

// Checkboxes pour Axe 2 - Alimentation
export const AXE2_CHECKBOXES = [
  { id: "repas_calme", label: "Prendre les repas dans le calme, assis, sans distraction ni écran." },
  { id: "horaires_reguliers", label: "Respecter des horaires de repas réguliers afin de soutenir la digestion." },
  { id: "limiter_grignotage", label: "Limiter le grignotage entre les repas, si possible." },
  { id: "adapter_quantites", label: "Adapter les quantités alimentaires aux besoins et à la sensation de satiété." },
  { id: "alleger_soir", label: "Alléger le repas du soir pour faciliter la digestion et le sommeil." },
  { id: "alimentation_simple", label: "Privilégier une alimentation simple, variée et peu transformée." },
  { id: "produits_frais", label: "Favoriser les produits frais, de saison et de qualité." },
  { id: "legumes_quotidiens", label: "Introduire quotidiennement des légumes, crus ou cuits selon la tolérance." },
  { id: "mastiquer_lentement", label: "Mastiquer lentement et suffisamment chaque bouchée." },
  { id: "observer_reactions", label: "Observer les réactions digestives afin d'adapter l'alimentation si nécessaire." },
  { id: "boire_eau", label: "Boire de l'eau régulièrement tout au long de la journée." },
  { id: "limiter_boissons_repas", label: "Limiter les boissons pendant les repas pour ne pas diluer les sucs digestifs." },
];

// Checkboxes pour Axe 3 - Activité physique
export const AXE3_CHECKBOXES = [
  { id: "mouvement_quotidien", label: "Intégrer du mouvement quotidiennement, même de façon modérée." },
  { id: "activite_adaptee", label: "Choisir une activité physique adaptée au niveau d'énergie du moment." },
  { id: "regularite", label: "Privilégier la régularité plutôt que l'intensité." },
  { id: "petit_pas", label: "Commencer par le plus petit pas possible afin de favoriser l'adhésion." },
  { id: "augmenter_progressivement", label: "Augmenter progressivement la durée ou la fréquence de l'activité." },
  { id: "respecter_limites", label: "Respecter les limites du corps et éviter toute douleur." },
  { id: "activites_plaisir", label: "Favoriser des activités apportant du plaisir et du bien-être." },
];

interface VitalityReportFormProps {
  data: VitalityReportData;
  onDataChange: (data: VitalityReportData) => void;
}

export function VitalityReportForm({ data, onDataChange }: VitalityReportFormProps) {
  const updateField = <K extends keyof VitalityReportData>(field: K, value: VitalityReportData[K]) => {
    onDataChange({ ...data, [field]: value });
  };

  const updateCheckbox = (axe: 'axe1_checkboxes' | 'axe2_checkboxes' | 'axe3_checkboxes', id: string, checked: boolean) => {
    const newCheckboxes = { ...data[axe], [id]: checked };
    onDataChange({ ...data, [axe]: newCheckboxes });
  };

  const addComplement = () => {
    const newComplement = {
      id: `comp-${Date.now()}`,
      nom: "",
      forme: "",
      posologie: "",
      moment_prise: "",
      duree: "",
    };
    updateField('complements', [...data.complements, newComplement]);
  };

  const updateComplement = (id: string, field: string, value: string) => {
    const updated = data.complements.map((c) =>
      c.id === id ? { ...c, [field]: value } : c
    );
    updateField('complements', updated);
  };

  const removeComplement = (id: string) => {
    if (data.complements.length === 1) return;
    updateField('complements', data.complements.filter((c) => c.id !== id));
  };

  const renderCheckboxGroup = (
    checkboxes: { id: string; label: string }[],
    axe: 'axe1_checkboxes' | 'axe2_checkboxes' | 'axe3_checkboxes'
  ) => (
    <div className="space-y-3 mt-4">
      {checkboxes.map((cb) => (
        <div key={cb.id} className="flex items-start gap-3 p-3 bg-muted/20 rounded-lg hover:bg-muted/30 transition-colors">
          <Checkbox
            id={`${axe}-${cb.id}`}
            checked={data[axe][cb.id] === true}
            onCheckedChange={(checked) => updateCheckbox(axe, cb.id, checked === true)}
            className="mt-0.5"
          />
          <label
            htmlFor={`${axe}-${cb.id}`}
            className="text-sm cursor-pointer leading-relaxed flex-1"
          >
            {cb.label}
          </label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Objectif de l'accompagnement */}
      <Card className="shadow-card border-primary/20">
        <CardHeader className="bg-primary/5">
          <CardTitle className="flex items-center gap-2 text-lg text-primary">
            <Target className="w-5 h-5" />
            Objectif de l'accompagnement
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="motif_principal" className="font-medium">
              Motif principal de la consultation
            </Label>
            <Textarea
              id="motif_principal"
              value={data.motif_principal}
              onChange={(e) => updateField('motif_principal', e.target.value)}
              placeholder="Décrivez le motif principal de la consultation..."
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="objectifs_definis" className="font-medium">
              Objectif(s) défini(s) avec le consultant
            </Label>
            <Textarea
              id="objectifs_definis"
              value={data.objectifs_definis}
              onChange={(e) => updateField('objectifs_definis', e.target.value)}
              placeholder="Quels objectifs avez-vous définis ensemble..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* AXE 1 – Hygiène vitale */}
      <Card className="shadow-card">
        <CardHeader className="bg-amber-50 dark:bg-amber-900/10">
          <CardTitle className="flex items-center gap-2 text-lg text-amber-700 dark:text-amber-400">
            <Sun className="w-5 h-5" />
            AXE 1 – Hygiène vitale
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="axe1_objectif" className="font-medium">Objectif</Label>
            <Input
              id="axe1_objectif"
              value={data.axe1_objectif}
              onChange={(e) => updateField('axe1_objectif', e.target.value)}
              placeholder="Objectif pour cet axe..."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="axe1_conseils" className="font-medium">Conseils</Label>
            <Textarea
              id="axe1_conseils"
              value={data.axe1_conseils}
              onChange={(e) => updateField('axe1_conseils', e.target.value)}
              placeholder="Conseils personnalisés..."
              rows={3}
            />
          </div>

          <Separator className="my-4" />
          <p className="text-sm text-muted-foreground font-medium">
            Cochez les recommandations applicables :
          </p>
          {renderCheckboxGroup(AXE1_CHECKBOXES, 'axe1_checkboxes')}
        </CardContent>
      </Card>

      {/* AXE 2 – Alimentation */}
      <Card className="shadow-card">
        <CardHeader className="bg-green-50 dark:bg-green-900/10">
          <CardTitle className="flex items-center gap-2 text-lg text-green-700 dark:text-green-400">
            <Utensils className="w-5 h-5" />
            AXE 2 – Alimentation
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="axe2_objectif" className="font-medium">Objectif</Label>
            <Input
              id="axe2_objectif"
              value={data.axe2_objectif}
              onChange={(e) => updateField('axe2_objectif', e.target.value)}
              placeholder="Objectif pour cet axe..."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="axe2_conseils" className="font-medium">Conseils</Label>
            <Textarea
              id="axe2_conseils"
              value={data.axe2_conseils}
              onChange={(e) => updateField('axe2_conseils', e.target.value)}
              placeholder="Conseils personnalisés..."
              rows={3}
            />
          </div>

          <Separator className="my-4" />
          <p className="text-sm text-muted-foreground font-medium">
            Cochez les recommandations applicables :
          </p>
          {renderCheckboxGroup(AXE2_CHECKBOXES, 'axe2_checkboxes')}
        </CardContent>
      </Card>

      {/* AXE 3 – Activité physique */}
      <Card className="shadow-card">
        <CardHeader className="bg-blue-50 dark:bg-blue-900/10">
          <CardTitle className="flex items-center gap-2 text-lg text-blue-700 dark:text-blue-400">
            <Activity className="w-5 h-5" />
            AXE 3 – Activité physique
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="axe3_objectif" className="font-medium">Objectif</Label>
            <Input
              id="axe3_objectif"
              value={data.axe3_objectif}
              onChange={(e) => updateField('axe3_objectif', e.target.value)}
              placeholder="Objectif pour cet axe..."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="axe3_recommandations" className="font-medium">Recommandations</Label>
            <Textarea
              id="axe3_recommandations"
              value={data.axe3_recommandations}
              onChange={(e) => updateField('axe3_recommandations', e.target.value)}
              placeholder="Recommandations personnalisées..."
              rows={4}
            />
          </div>

          <Separator className="my-4" />
          <p className="text-sm text-muted-foreground font-medium">
            Cochez les recommandations applicables :
          </p>
          {renderCheckboxGroup(AXE3_CHECKBOXES, 'axe3_checkboxes')}
        </CardContent>
      </Card>

      {/* Compléments / soutiens naturels */}
      <Card className="shadow-card">
        <CardHeader className="bg-purple-50 dark:bg-purple-900/10">
          <CardTitle className="flex items-center justify-between text-lg text-purple-700 dark:text-purple-400">
            <div className="flex items-center gap-2">
              <Pill className="w-5 h-5" />
              Compléments / soutiens naturels
            </div>
            <button
              type="button"
              onClick={addComplement}
              className="text-sm px-3 py-1 bg-purple-100 dark:bg-purple-900/30 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
            >
              + Ajouter
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {data.complements.map((complement, index) => (
            <div key={complement.id} className="p-4 border rounded-lg bg-muted/20 space-y-4 relative">
              {data.complements.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeComplement(complement.id)}
                  className="absolute top-2 right-2 text-xs text-destructive hover:underline"
                >
                  Supprimer
                </button>
              )}
              <p className="text-sm font-medium text-muted-foreground">
                Complément {index + 1}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Nom</Label>
                  <Input
                    value={complement.nom}
                    onChange={(e) => updateComplement(complement.id, 'nom', e.target.value)}
                    placeholder="Nom du complément..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Forme</Label>
                  <Input
                    value={complement.forme}
                    onChange={(e) => updateComplement(complement.id, 'forme', e.target.value)}
                    placeholder="Gélule, tisane, huile..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Posologie</Label>
                  <Input
                    value={complement.posologie}
                    onChange={(e) => updateComplement(complement.id, 'posologie', e.target.value)}
                    placeholder="1 gélule, 2 gouttes..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Moment de prise</Label>
                  <Input
                    value={complement.moment_prise}
                    onChange={(e) => updateComplement(complement.id, 'moment_prise', e.target.value)}
                    placeholder="Matin, soir, avant repas..."
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm">Durée</Label>
                  <Input
                    value={complement.duree}
                    onChange={(e) => updateComplement(complement.id, 'duree', e.target.value)}
                    placeholder="3 semaines, 1 mois..."
                  />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Autres */}
      <Card className="shadow-card">
        <CardHeader className="bg-slate-50 dark:bg-slate-900/10">
          <CardTitle className="flex items-center gap-2 text-lg text-slate-700 dark:text-slate-400">
            <FileText className="w-5 h-5" />
            Autres
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="analyses_biologiques" className="font-medium">
              Analyses biologiques (en accord avec le médecin)
            </Label>
            <Textarea
              id="analyses_biologiques"
              value={data.analyses_biologiques}
              onChange={(e) => updateField('analyses_biologiques', e.target.value)}
              placeholder="Analyses à réaliser..."
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ressources_conseillees" className="font-medium">
              Ressources conseillées
            </Label>
            <Textarea
              id="ressources_conseillees"
              value={data.ressources_conseillees}
              onChange={(e) => updateField('ressources_conseillees', e.target.value)}
              placeholder="Livres, podcasts, applications..."
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="orientations_professionnels" className="font-medium">
              Orientations vers d'autres professionnels
            </Label>
            <Textarea
              id="orientations_professionnels"
              value={data.orientations_professionnels}
              onChange={(e) => updateField('orientations_professionnels', e.target.value)}
              placeholder="Ostéopathe, psychologue, médecin..."
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="remarques_complementaires" className="font-medium">
              Remarques complémentaires
            </Label>
            <Textarea
              id="remarques_complementaires"
              value={data.remarques_complementaires}
              onChange={(e) => updateField('remarques_complementaires', e.target.value)}
              placeholder="Notes additionnelles..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
